import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";

import { CanDeactivateClass } from "@shared/classes/can-deactivate.class";
import { TextConstants } from "@shared/constants";
import { LanguageEnum, RouteEnum } from "@shared/enums";
import { LocalizationService, UsersService } from "@shared/services";
import { ConfirmDialogSubmitButtonMode } from "@shared/types";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-user-settings-profile",
  templateUrl: "./user-settings-profile.component.html",
  styleUrls: ["./user-settings-profile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsProfileComponent extends CanDeactivateClass implements OnInit {
  formGroup: UntypedFormGroup;

  readonly routingEnum = RouteEnum;

  readonly isLoading = signal(true);

  readonly hasLanguageChanged = signal(false);

  readonly newLanguage = signal<LanguageEnum>(null);

  readonly translations = {
    title: $localize`:@@shared.profile:Profile`,
    titleBackText: TextConstants.BACK_TO_USER_SETTINGS,
    language: $localize`:@@user-settings-profile.language:Language`,
    yourProfile: $localize`:@@user-settings-profile.yourprofile:Your profile`,
    languageSection: $localize`:@@user-settings-profile.languagesection:Language`,
    firstName: $localize`:@@shared.firstname:First name`,
    lastName: $localize`:@@shared.lastname:Last name`,
    email: TextConstants.EMAIL,
    customizeLanguage: $localize`:@@user-settings-profile.customizelanguage:Customize your language.`,
  };

  private readonly localizationService = inject(LocalizationService);

  private readonly usersService = inject(UsersService);

  readonly isSubmitButtonDisabled = computed(() => !this.hasLanguageChanged());

  async ngOnInit(): Promise<void> {
    await this.setupForm();
  }

  onLanguageChanged(languageChanged: { hasLanguageChanged: boolean; newLanguage: LanguageEnum }) {
    this.hasLanguageChanged.set(languageChanged.hasLanguageChanged);
    this.newLanguage.set(languageChanged.newLanguage);
  }

  async onSave() {
    this.isLoading.set(true);

    //todo once we're able to save user email -> userData needs to be updated with that email.

    if (this.hasLanguageChanged()) {
      await this.localizationService.changeLanguage(this.newLanguage());
    }
    this.isLoading.set(false);
  }

  protected override requiresConfirmation() {
    return this.hasLanguageChanged();
  }

  protected override async saveBeforeClosing() {
    await this.onSave();
  }

  protected override submitButtonMode(): ConfirmDialogSubmitButtonMode {
    return "hidden";
  }

  private setupForm = async (): Promise<void> => {
    const user = await this.usersService.get(this.authenticationService.getUserId());

    if (user) {
      this.formGroup = new UntypedFormGroup({
        firstName: new UntypedFormControl({ value: user.firstName, disabled: true }, [
          CustomValidators.required,
        ]),
        lastName: new UntypedFormControl({ value: user.lastName, disabled: true }, [
          CustomValidators.required,
        ]),
        email: new UntypedFormControl({ value: user.emailAddress, disabled: true }, [
          CustomValidators.required,
          CustomValidators.email,
        ]),
      });
      this.isLoading.set(false);
    }
  };
}
